//hooks
import { useEffect, useRef } from "react";
import { useDrag, useDrop } from "react-dnd";

//components
import DragAndDropControlContent from "./DragAndDropControlContent";
import SelectButtonList from "@/components/elements/global/lists/SelectButtonList/SelectButtonList";
import Icon from "@/components/elements/icons/Icons";
import useDetectClick from "@/components/hooks/useDetectClick";


//interfaces
import { IconLibrary } from "@/interfaces/IconLibrary";
import { ISelectButton } from "@/interfaces/selectButtonList";
import { ITableRows } from "@/interfaces/simpleTable";

interface IDragAndDropRow {
    containerRef: any,
    isHighlightedRow?: boolean,
    row: ITableRows,
    index: number,
    hoverIndex: number | null,
    controlIndex: number | undefined,
    setControlIndex: any,
    moveRow: any,
    rowContentLength: number,
    setHoverIndex: any,
    openMenu: boolean,
    setOpenMenu: any,
    dragAndDropAction?:(newIndex: number) => void,
    isPaginated?: boolean,
    paginationInformation?:{
        totalPages?: number,
        currentPage?: number,
        resultsPerPage?: number
        totalItems?: number;
    }
}


const DragAndDropRow = ({ containerRef, row, isHighlightedRow, index, hoverIndex, controlIndex, setControlIndex, moveRow, rowContentLength, setHoverIndex, openMenu, setOpenMenu, dragAndDropAction, isPaginated, paginationInformation}: IDragAndDropRow) => {
    const ref = useRef<HTMLTableRowElement>(null);
    const controlCellRef = useRef<HTMLTableCellElement>(null)
    const menuRef = useRef<HTMLDivElement>(null);
    const isNotPageOne = paginationInformation?.currentPage > 1;
    const isNotLastPage = paginationInformation?.currentPage < paginationInformation?.totalPages;
    const previousIndex = index-1;
    const nextIndex = index+1;
    const lastIndex = paginationInformation.totalItems;
    const isFirstItemButNotPageOne = index === 0 && isNotPageOne;

    const paginationPreviousIndex = isNotPageOne ? (((paginationInformation.currentPage -1) * paginationInformation.resultsPerPage) + (previousIndex)) : (previousIndex);
    const paginationNextIndex = isNotPageOne ? (((paginationInformation.currentPage -1) * paginationInformation.resultsPerPage) + (nextIndex)) : (nextIndex);
    const moveToNextPageIndex =  (paginationInformation.currentPage * paginationInformation.resultsPerPage);
    const moveToPreviousPageIndex = ((paginationInformation.currentPage -2) * (paginationInformation.resultsPerPage));
    const dragAndDropControls: ISelectButton[] = isPaginated 
        ?   [ 
                index !== 0 || (isFirstItemButNotPageOne) ? { content: <DragAndDropControlContent iconName={IconLibrary.ORDER_TOP} text={"Send to top"} />,  onClick: () => handleButtonMove(index, 0, isNotPageOne)} : undefined,
                index !== 0 || (isFirstItemButNotPageOne) ? { content: <DragAndDropControlContent iconName={IconLibrary.ORDER_UP} text={"Move up"} />, onClick: () => handleButtonMove(index, paginationPreviousIndex, isFirstItemButNotPageOne)} : undefined,
                index !== rowContentLength - 1 ? { content: <DragAndDropControlContent iconName={IconLibrary.ORDER_DOWN} text={"Move down"} />, onClick: () => handleButtonMove(index, paginationNextIndex, false) }: undefined,
                index !== rowContentLength - 1 ? { content: <DragAndDropControlContent iconName={IconLibrary.ORDER_BOTTOM} text={"Send to bottom"} />, onClick: () => handleButtonMove(index, lastIndex, isNotLastPage) }: undefined,
                paginationInformation?.currentPage > 1 ? { content: <DragAndDropControlContent iconName={IconLibrary.ORDER_UP} text={"Move to previous page"} />, onClick: () => handleButtonMove(index, moveToPreviousPageIndex, true)  } : undefined,
                paginationInformation?.totalPages > 1 && isNotLastPage ? { content: <DragAndDropControlContent iconName={IconLibrary.ORDER_DOWN} text={"Move to next page"} />, onClick: () => handleButtonMove(index, moveToNextPageIndex, true)}: undefined,
            ].filter(control => control)
        :   [ 
                index !== 0 ? { content: <DragAndDropControlContent iconName={IconLibrary.ORDER_TOP} text={"Send to top"} />,  onClick: () => handleButtonMove(index, 0, false)} : undefined,
                index !== 0 ? { content: <DragAndDropControlContent iconName={IconLibrary.ORDER_UP} text={"Move up"} />, onClick: () => handleButtonMove(index, previousIndex, false)} : undefined,
                index !== (paginationInformation.totalItems - 1) ? { content: <DragAndDropControlContent iconName={IconLibrary.ORDER_DOWN} text={"Move down"} />, onClick: () => handleButtonMove(index, nextIndex, false) }: undefined,
                index !== (paginationInformation.totalItems - 1) ? { content: <DragAndDropControlContent iconName={IconLibrary.ORDER_BOTTOM} text={"Send to bottom"} />, onClick: () => handleButtonMove(index, lastIndex, false) }: undefined,
            ].filter(control => control);
    
    const [{ isDragging }, drag, preview] = useDrag({
        type: "ROW",
        item: { index },
        collect: (monitor) => ({ isDragging: monitor.isDragging()}),
        end: () => { setHoverIndex(null) }
    });

    const [, drop] = useDrop({
        accept: "ROW",
        hover(item: { index: number }) {
            if (!ref.current) { return }

            const dragIndex = item.index;
            const hoverIndex = index;
            if (dragIndex === hoverIndex) { return }

            moveRow(dragIndex, hoverIndex, false);
            setHoverIndex(hoverIndex);
            item.index = hoverIndex;
        },
        
        drop() {
            setHoverIndex(null);
            dragAndDropAction?.(hoverIndex)
        }
    });
    
    useEffect(() => {
        preview(drop(ref));
    }, [preview, drop]);
    
    useEffect(() => {
        if(isDragging) {
            document.body.style.cursor = 'grabbing'
        }
        else if(!isDragging){
           document.body.style.cursor = 'inherit'
        }
    }, [isDragging])

    useEffect(() => {
        const handleDragLeave = (event: DragEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.relatedTarget as Node)) {
                setHoverIndex(null);
            }
        };

        if (containerRef.current) { containerRef.current.addEventListener('dragleave', handleDragLeave); }

        return () => {
            if (containerRef.current) { containerRef.current.removeEventListener('dragleave', handleDragLeave); }
        };
    }, [containerRef]);

    useDetectClick("control-cell", (clickedInCal) => {
        if(!clickedInCal) {
            setOpenMenu(false);
        }
    });

    function handleButtonMove(index: number, newIndex: number, isNewPageMove?: boolean){
        dragAndDropAction?.(newIndex)
        moveRow(index, newIndex, isNewPageMove)
    }


    return (
        <tr ref={ref} className={`drag-item ${isDragging && "is-dragging"}  ${index === hoverIndex && "is-hovering"}  ${isHighlightedRow ? "highlighed-row-style" : ""}`}>
            {row.content.map((cell: any, cellIndex: number) => (
                <td key={cellIndex}>{cell}</td>
            ))}

            <td className="control-cell"  ref={controlCellRef}>
                <button type='button' ref={drag} className="drag-and-drop-controller" aria-hidden="true" tabIndex={-1}><Icon iconName={IconLibrary.DRAG} color="#999EA5" sizeInPx="12"/></button>

                <button type='button' className="simple-table-menu" aria-label="Open menu" onClick={() => { setOpenMenu(!openMenu); setControlIndex(index); }} tabIndex={0}> <Icon iconName={IconLibrary.MENU_MORE} color="#343D4C" sizeInPx="12"/></button>
                        
                {controlIndex === index && openMenu && (
                    <div ref={menuRef}>
                        <SelectButtonList className="drag-and-drop-list" allButtonOnClick={()=> setOpenMenu(false)} buttons={[...dragAndDropControls, ...row.controls ?? []]} />
                    </div>
                )}
            </td>
        </tr>
    );
};

export default DragAndDropRow;